import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Button } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import ConfirmationModal from '../components/ConfirmationModal';
import UploadSongFileModal from '../components/UploadSongFileModal';
import CreateSongModal from '../components/CreateSongModal'; // Импортируем новое модальное окно
import { toast } from 'react-toastify';

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a href="" ref={ref} onClick={(e) => { e.preventDefault(); onClick(e); }} className="text-dark p-2">
      {children}
    </a>
));

const SongListPage = () => {
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { isAuthenticated, user: currentUser } = useAuth();
    const { canCreate, canRead, canDelete, loading: permissionsLoading } = usePermissions('song');
    const navigate = useNavigate();

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [songToDelete, setSongToDelete] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false); // Состояние для нового модального окна

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const songsPerPage = parseInt(process.env.REACT_APP_SONGS_PER_PAGE, 10) || 20;

    const observer = useRef();
    const lastSongElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchSongs = useCallback(() => {
        if (!isAuthenticated) {
            setLoading(false);
            setError('Вам нужно войти в систему, чтобы просматривать э��у страницу.');
            return;
        }
        setLoading(true);
        setError('');
        const skip = (page - 1) * songsPerPage;
        apiClient.get(`/songs/?skip=${skip}&limit=${songsPerPage}`)
            .then(response => {
                setSongs(prevSongs => {
                    const existingIds = new Set(prevSongs.map(s => s.id));
                    const newSongs = response.data.songs.filter(s => !existingIds.has(s.id));
                    return [...prevSongs, ...newSongs];
                });
                setHasMore((songs.length + response.data.songs.length) < response.data.total_count);
            })
            .catch(err => {
                setError('Ошибка загрузки списка песен');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, isAuthenticated, songsPerPage, songs.length]);

    // Effect to reset list and fetch data
    useEffect(() => {
        setSongs([]);
        setPage(1);
        setHasMore(true);
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchSongs();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, isAuthenticated]);

    const handleUploadSuccess = (newSong) => {
        setShowUploadModal(false);
        navigate(`/songs/${newSong.id}`);
    };

    const handleSongCreated = (newSong) => {
        setShowCreateModal(false);
        navigate(`/songs/${newSong.id}`);
    };

    const loadSongOptions = (inputValue, callback) => {
        if (!inputValue) {
            return callback([]);
        }
        apiClient.get(`/songs/search?q=${inputValue}`)
            .then(response => {
                const options = response.data.map(song => ({
                    value: song.id,
                    label: song.title
                }));
                callback(options);
            });
    };

    const handleSearchChange = (option) => {
        if (option && option.value) {
            navigate(`/songs/${option.value}`);
        }
    };

    const handleDeleteClick = (song) => {
        setSongToDelete(song);
        setShowConfirmDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (songToDelete) {
            try {
                await apiClient.delete(`/songs/${songToDelete.id}`);
                toast.success('Песня успешно удалена!');
                setSongs(prevSongs => prevSongs.filter(song => song.id !== songToDelete.id));
            } catch (err) {
                toast.error('Ошибка удаления песни.');
            } finally {
                setShowConfirmDeleteModal(false);
                setSongToDelete(null);
            }
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDeleteModal(false);
        setSongToDelete(null);
    };

    

    if ((loading && page === 1) || permissionsLoading) return <div className="text-center">Загрузка...</div>;
    if (error && !songs.length) return <div className="alert alert-danger">{error}</div>;

    return (
        <>
            <h1 className="h2">База песен</h1>

            {canRead && (
                <div className="card mb-4">
                    <div className="card-header">Поиск</div>
                    <div className="card-body">
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <AsyncSelect
                                    isClearable
                                    loadOptions={loadSongOptions}
                                    onChange={handleSearchChange}
                                    placeholder="Начните вводить название для поиска..."
                                    noOptionsMessage={({ inputValue }) => (inputValue ? 'Ничего не найдено' : 'Начните ввод для поиска')}
                                />
                            </div>
                            {canCreate && (
                                <>
                                    <Button variant="primary" onClick={() => setShowCreateModal(true)} title="Создать новую песню">
                                        <i className="fas fa-plus"></i>
                                    </Button>
                                    <Button variant="outline-primary" onClick={() => setShowUploadModal(true)} title="Создать песню из файла">
                                        <i className="fas fa-upload"></i>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>Список песен</span>
                </div>
                <ul className="list-group list-group-flush">
                    {songs.length > 0 ? (
                        songs.map((song, index) => {
                            const isLastElement = songs.length === index + 1;
                            const isOwnSong = currentUser && song.organization_id === currentUser.organization_id;

                            return (
                                <li ref={isLastElement ? lastSongElementRef : null} key={song.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <div className="d-flex flex-column">
                                        <Link to={`/songs/${song.id}`} className="fw-bold text-decoration-none">{song.title}</Link>
                                        <small className="text-muted">
                                            Добавлено: {song.created_at ? new Date(song.created_at).toLocaleDateString() : '-'}
                                        </small>
                                    </div>
                                    {canDelete && isOwnSong && (
                                        <Dropdown>
                                            <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${song.id}`}>
                                                <i className="fas fa-ellipsis-v"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => handleDeleteClick(song)} className="text-danger">Удалить</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </li>
                            );
                        })
                    ) : (
                        !loading && <li className="list-group-item"><p className="card-text text-center">Песен пока нет.</p></li>
                    )}
                </ul>
                {loading && <div className="text-center p-3">Загрузка...</div>}
                {!loading && !hasMore && songs.length > 0 && <div className="text-center text-muted p-3">Больше песен нет</div>}
            </div>

            <ConfirmationModal
                show={showConfirmDeleteModal}
                title="Подтвердить удаление"
                message={`Вы уверены, что хотите удалить песню "${songToDelete?.title}"? Все связанные с ней файлы и аранжировки будут удалены.`}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmText="Удалить"
                cancelText="Отмена"
            />

            <UploadSongFileModal 
                show={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUploadSuccess={handleUploadSuccess}
            />

            <CreateSongModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSongCreated={handleSongCreated}
            />
        </>
    );
};

export default SongListPage;